// Reading captions out of a file somebody else's tool wrote.
//
// The point of this module is the workflow it serves: draft twenty captions in
// ChatGPT or Gemini, paste them into a spreadsheet, bring the spreadsheet here.
// The material is written elsewhere; we are only the library it lands in.
//
// ---------------------------------------------------------------- no library
//
// There is no xlsx dependency and that is deliberate. SheetJS's community
// edition is free (Apache-2.0), but the last release published to npm is
// 0.18.5, which carries two known advisories; the fixed builds live only on the
// vendor's own CDN, which means a tarball URL in package.json rather than a
// registry version. In an app going through Meta and Google review, that is a
// line item we would have to defend, and it would not read `.md` anyway.
//
// A .xlsx is a ZIP of XML, and every browser we support can now inflate a raw
// deflate stream. So the whole reader is: find the entry, inflate it, parse the
// XML with the parser the platform already ships. About two hundred lines, no
// supply chain, and an interface narrow enough that swapping SheetJS in behind
// it later is one file.
//
// What we do NOT handle, on purpose: .xls (the old binary format), password
// protection, and ZIP64. Each of those returns a message naming the file and
// telling the user to save as .csv, which is one menu item away in Excel.

/** Everything the review step needs to account for every row in the file. */
export interface ParsedFile {
  /** In file order, trimmed, non-empty, within the length the database takes. */
  captions: string[];
  /** What we read, in words: "Sheet1, column A" or "18 blocks". */
  from: string;
  /** Rows that were empty. Counted rather than listed — nobody wants that list. */
  blank: number;
  /** Rows too long to store. Listed, because each one needs a decision. */
  tooLong: string[];
  /** The first row, if it looked like a column heading and was dropped. */
  headerSkipped: string | null;
}

/** `content_drafts.body` is `char_length between 1 and 10000`. Refuse here, not there. */
export const MAX_CAPTION_CHARS = 10_000;

/** One import, one sitting. Past this the review step stops being reviewable. */
export const MAX_CAPTIONS_PER_IMPORT = 500;

/**
 * The file the user picks. Generous for its purpose — a spreadsheet of five
 * hundred captions is well under a megabyte — and the first of three limits
 * that keep a hostile file from taking the tab down with it.
 */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/**
 * Everything we inflate out of one .xlsx, across every entry.
 *
 * A ZIP says how big each entry expands to and then expands to whatever it
 * likes; deflate reaches roughly 1000:1 on repetitive input, so a 300 KB file
 * that claims to be a spreadsheet can hand back 300 MB and take the better part
 * of a gigabyte of memory doing it. Measured, not assumed — see sheet_smoke.ts.
 *
 * Nothing here is malice-specific: the same cap stops an honestly enormous
 * workbook from freezing somebody's browser, which is the more likely way this
 * gets hit.
 */
export const MAX_INFLATED_BYTES = 48 * 1024 * 1024;

const HEADER_WORDS = new Set([
  "caption", "captions", "post", "posts", "text", "content", "body", "copy",
  "description", "descriptions", "message", "messages",
]);

export function isSupportedFile(name: string): boolean {
  return /\.(xlsx|csv|tsv|md|markdown|txt)$/i.test(name);
}

/**
 * Read a file the user picked. Never throws for a *readable* file that happens
 * to be empty — that is a result the review step reports, not an error.
 */
export async function parseCaptionFile(file: File): Promise<ParsedFile> {
  const name = file.name.toLowerCase();

  // Before a byte is read. Every later limit is a backstop for a file that got
  // past a check; this one is the check.
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1024 / 1024)} MB, and captions are read from ` +
      `files up to ${MAX_FILE_BYTES / 1024 / 1024} MB. Split it, or delete the columns you ` +
      `are not importing.`,
    );
  }

  if (name.endsWith(".xls")) {
    throw new Error(
      "That is the old .xls format, which cannot be read in a browser. Open it in Excel and " +
      "choose File → Save As → Excel Workbook (.xlsx), or CSV.",
    );
  }
  if (name.endsWith(".xlsx")) {
    return await parseXlsx(new Uint8Array(await file.arrayBuffer()));
  }
  if (name.endsWith(".csv") || name.endsWith(".tsv")) {
    return parseDelimited(stripBom(await file.text()), name.endsWith(".tsv") ? "\t" : ",");
  }
  if (/\.(md|markdown|txt)$/.test(name)) {
    return parseProse(stripBom(await file.text()));
  }
  throw new Error(
    `${file.name} is not a file type we can read. Use .xlsx, .csv, .md or .txt.`,
  );
}

const stripBom = (s: string) => s.replace(/^\uFEFF/, "");

// ---------------------------------------------------------------- CSV / TSV

/**
 * RFC 4180, and only the first column.
 *
 * The full quoting rules are here rather than a `split(",")` because a caption
 * is exactly the kind of text that contains a comma, and a naive split turns
 * one good caption into two broken ones without saying so.
 */
export function parseDelimited(text: string, delimiter: string): ParsedFile {
  const rows: string[] = [];
  let field = "";
  let quoted = false;
  let rowDone = false;
  let takenThisRow = false;

  const endField = () => {
    // Only the first field of each row is a caption; the rest are somebody's
    // notes and are dropped without comment.
    if (!takenThisRow) { rows.push(field); takenThisRow = true; }
    field = "";
  };
  const endRow = () => { endField(); takenThisRow = false; rowDone = true; };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }  // escaped quote
        else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"' && field === "") { quoted = true; continue; }
    if (ch === delimiter) { endField(); continue; }
    if (ch === "\n") { endRow(); continue; }
    if (ch === "\r") continue;
    field += ch;
  }
  // A file not ending in a newline still has a last row.
  if (field !== "" || !rowDone) endRow();

  return account(rows, `column 1 of ${rows.length} rows`);
}

// ---------------------------------------------------------------- Markdown

/**
 * One document, many captions — the shape an LLM answer actually arrives in.
 *
 * Four separators, tried in the order a document is likely to use them. The
 * review step exists precisely because this is a guess: the user sees what the
 * guess produced before anything is saved.
 */
export function parseProse(raw: string): ParsedFile {
  const text = raw.replace(/\r\n?/g, "\n").trim();
  if (!text) return account([], "an empty file");

  let blocks: string[];
  let how: string;

  if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/m.test(text)) {
    blocks = text.split(/^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/m);
    how = "blocks separated by a horizontal rule";
  } else if (/^#{1,6}\s+/m.test(text)) {
    // Drop the headings themselves: they are nearly always "Post 1", "Caption
    // 2" — scaffolding from the tool that wrote the file, not caption text.
    blocks = text.split(/^#{1,6}\s+.*$/m);
    how = "sections under each heading";
  } else if (/^\s{0,3}(?:[-*+]|\d+[.)])\s+/m.test(text)) {
    blocks = text.split(/^(?=\s{0,3}(?:[-*+]|\d+[.)])\s+)/m);
    how = "list items";
  } else {
    blocks = text.split(/\n{2,}/);
    how = "paragraphs";
  }

  return account(blocks.map(tidyBlock), `${how} — ${blocks.length} found`);
}

/** Strip the marks that belong to the document, not to the caption. */
function tidyBlock(block: string): string {
  return block
    .replace(/^```[^\n]*\n?/, "").replace(/\n?```\s*$/, "")   // a fenced block
    .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+/, "")              // its list marker
    .split("\n").map((l) => l.replace(/^\s{0,3}>\s?/, "")).join("\n")  // blockquote
    .trim()
    .replace(/^"(.*)"$/s, "$1")                               // wrapping quotes
    .replace(/^“(.*)”$/s, "$1")
    .trim();
}

// ---------------------------------------------------------------- XLSX

export async function parseXlsx(bytes: Uint8Array): Promise<ParsedFile> {
  const zip = await readZip(bytes);

  const { path: sheetPath, name: sheetName } = firstSheet(zip);
  const sheetXml = zip.get(sheetPath);
  if (!sheetXml) {
    throw new Error(
      "That workbook has no readable sheet. Save it as CSV from Excel and try again.",
    );
  }

  const shared = zip.has("xl/sharedStrings.xml")
    ? sharedStrings(zip.get("xl/sharedStrings.xml")!)
    : [];

  const doc = xml(sheetXml, "worksheet");
  const values: string[] = [];

  for (const c of Array.from(doc.getElementsByTagName("c"))) {
    const ref = c.getAttribute("r") ?? "";
    // Column A only. "one column" is the contract; a second column is notes.
    if (ref.replace(/\d+/g, "").toUpperCase() !== "A") continue;

    const type = c.getAttribute("t");
    if (type === "inlineStr") {
      values.push(textOf(c.getElementsByTagName("is")[0]));
      continue;
    }
    const v = c.getElementsByTagName("v")[0];
    const text = v?.textContent ?? "";
    if (type === "s") {
      values.push(shared[Number(text)] ?? "");
    } else {
      // "str" is a formula result; no type at all is a number or a date serial.
      // Either way its text is the honest reading of what is in the cell.
      values.push(text);
    }
  }

  return account(values, `${sheetName}, column A — ${values.length} cells`);
}

/**
 * The first sheet in the workbook's own order, which is not always sheet1.xml,
 * along with the name printed on its tab — because "Sheet1, column A" and
 * "Captions, column A" are answers to different questions, and only the second
 * one lets somebody check we read the sheet they meant.
 */
function firstSheet(zip: Map<string, Uint8Array>): { path: string; name: string } {
  const book = zip.get("xl/workbook.xml");
  const rels = zip.get("xl/_rels/workbook.xml.rels");
  if (book && rels) {
    const sheet = xml(book, "workbook").getElementsByTagName("sheet")[0];
    const name = sheet?.getAttribute("name") ?? "the first sheet";
    const rid = sheet?.getAttribute("r:id") ?? sheet?.getAttribute("id");
    if (rid) {
      for (const r of Array.from(xml(rels, "Relationships").getElementsByTagName("Relationship"))) {
        if (r.getAttribute("Id") !== rid) continue;
        const target = (r.getAttribute("Target") ?? "").replace(/^\/?(xl\/)?/, "");
        if (zip.has(`xl/${target}`)) return { path: `xl/${target}`, name };
      }
    }
  }
  return { path: "xl/worksheets/sheet1.xml", name: "the first sheet" };
}

function sharedStrings(data: Uint8Array): string[] {
  return Array.from(xml(data, "sst").getElementsByTagName("si")).map(textOf);
}

/** An `<si>` or `<is>` is one string, possibly broken into styled runs. */
function textOf(node: Element | undefined): string {
  if (!node) return "";
  return Array.from(node.getElementsByTagName("t")).map((t) => t.textContent ?? "").join("");
}

function xml(data: Uint8Array, expectedRoot: string): Element {
  const doc = new DOMParser().parseFromString(
    new TextDecoder().decode(data), "application/xml",
  );
  if (doc.getElementsByTagName("parsererror").length || doc.documentElement.tagName !== expectedRoot) {
    throw new Error("That workbook is not in a shape we can read. Save it as CSV and try again.");
  }
  return doc.documentElement;
}

// ---------------------------------------------------------------- the ZIP

/**
 * Just enough ZIP to get named entries out of an .xlsx.
 *
 * Read from the end, as the format intends: the end-of-central-directory record
 * points at the central directory, which names every entry and where its data
 * starts. Scanning forward for local headers instead is how you end up reading
 * a filename out of a compressed byte that happens to look like a signature.
 */
export async function readZip(bytes: Uint8Array): Promise<Map<string, Uint8Array>> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  let eocd = -1;
  // The comment field is at most 65535 bytes, so the record is within the last 64KiB+22.
  const from = Math.max(0, bytes.length - 0xFFFF - 22);
  for (let i = bytes.length - 22; i >= from; i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) {
    throw new Error("That file is not a valid .xlsx workbook — it is not a zip archive.");
  }

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  if (offset === 0xFFFFFFFF) {
    throw new Error("That workbook uses ZIP64, which we cannot read. Save it as CSV instead.");
  }

  const out = new Map<string, Uint8Array>();
  // Spent across every entry, so four entries of just-under-the-limit cannot
  // add up to the thing the limit exists to prevent.
  let budget = MAX_INFLATED_BYTES;

  for (let n = 0; n < count; n++) {
    const entry = offset;
    if (view.getUint32(entry, true) !== 0x02014b50) break;
    const method = view.getUint16(entry + 10, true);
    const compressed = view.getUint32(entry + 20, true);
    // What the entry claims it expands to. A lie costs nothing to tell, so this
    // is an early exit and not the enforcement — `inflateRaw` counts the bytes
    // that actually arrive.
    const declared = view.getUint32(entry + 24, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const localAt = view.getUint32(offset + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLen));
    offset += 46 + nameLen + extraLen + commentLen;

    // Only the three entries we actually read. An .xlsx carries themes, styles
    // and often every image in the book; inflating those is pure waste.
    if (!/^xl\/(workbook\.xml|sharedStrings\.xml|_rels\/workbook\.xml\.rels|worksheets\/.+\.xml)$/
          .test(name)) continue;

    // The local header repeats the name and extra fields, and its extra field
    // length is frequently different from the central one — so read it here
    // rather than trusting the copy above.
    if (view.getUint32(localAt, true) !== 0x04034b50) continue;
    const dataAt = localAt + 30
      + view.getUint16(localAt + 26, true)
      + view.getUint16(localAt + 28, true);
    const raw = bytes.subarray(dataAt, dataAt + compressed);

    if (declared > budget) throw bombError();

    if (method === 0) {
      if (raw.length > budget) throw bombError();
      budget -= raw.length;
      out.set(name, raw);
    } else if (method === 8) {
      const inflated = await inflateRaw(raw, budget);
      budget -= inflated.length;
      out.set(name, inflated);
    } else {
      throw new Error(
        "That workbook uses a compression method we cannot read. Save it as CSV instead.",
      );
    }
  }
  return out;
}

function bombError(): Error {
  return new Error(
    "That workbook expands to far more data than it appears to contain, which is how a " +
    "malicious file crashes a browser tab. Nothing was imported.",
  );
}

/**
 * Inflate, counting as we go and stopping the moment the budget is gone.
 *
 * Read chunk by chunk rather than with `new Response(stream).arrayBuffer()`,
 * which is shorter but buffers the whole output before anyone can object — by
 * the time it returns a number to check, the memory is already spent.
 */
async function inflateRaw(data: Uint8Array, budget: number): Promise<Uint8Array> {
  // Copied into a buffer TypeScript knows is not shared: a subarray of the file
  // carries `ArrayBufferLike`, which Blob will not take.
  const copy = new Uint8Array(data.length);
  copy.set(data);
  const reader = new Blob([copy.buffer]).stream()
    .pipeThrough(new DecompressionStream("deflate-raw"))
    .getReader();

  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.length;
      if (total > budget) {
        await reader.cancel();
        throw bombError();
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}

// ---------------------------------------------------------------- accounting

/**
 * Turn raw cells into the answer the review step shows.
 *
 * Every row the file contained is accounted for — kept, blank, dropped as a
 * heading, or too long — because "we parsed 21 of your 24 rows" is only
 * trustworthy if the other three are named.
 */
function account(raw: string[], from: string): ParsedFile {
  const rows = raw.map((r) => r.replace(/\r/g, "").trim());

  let headerSkipped: string | null = null;
  const first = rows[0];
  if (
    first && rows.length > 1 && first.length <= 40 &&
    HEADER_WORDS.has(first.toLowerCase().replace(/[^a-z]/g, ""))
  ) {
    headerSkipped = first;
    rows.shift();
  }

  const captions: string[] = [];
  const tooLong: string[] = [];
  let blank = 0;

  for (const row of rows) {
    if (!row) { blank++; continue; }
    if (row.length > MAX_CAPTION_CHARS) { tooLong.push(row); continue; }
    captions.push(row);
  }

  return { captions, from, blank, tooLong, headerSkipped };
}
